// Voicemail web: pure types, response readers and copy (docs/implementation/OPUS-VOICEMAIL.md section 2). Nothing
// here invents voicemail data: every message, greeting, setting and dry-run answer shown comes from the server.
//
// The web bundle imports only types from packages/contracts (its runtime pulls in TypeBox), so the few code lists
// below are narrow local copies, each checked against the contract type with `satisfies`.
import type * as C from '@bcio-voice/contracts';
import { isApiRequestError } from '../../lib/api.ts';
import { describeError, type ErrorView } from '../../lib/errorCopy.ts';

export const STATES = ['new', 'listened', 'archived'] as const satisfies readonly C.VoicemailState[];
export const FALLBACK_TRIGGERS = ['no_answer', 'busy', 'unavailable'] as const;
export const GREETING_SCOPES = ['number', 'ring_group', 'tenant_default'] as const;
export const FLOWS = ['greeting_beep_record', 'beep_record', 'missed_call'] as const;
/** VOICEMAIL_LIMITS (the parts the web uses). */
export const LIMITS = Object.freeze({ greetingMaxBytes: 1048576, greetingMinMs: 1000, greetingMaxMs: 60000, sampleRate: 8000, recordMaxSeconds: 180, discardBelowMs: 1000, pageSize: 50 });

export type VoicemailState = (typeof STATES)[number];
export type FallbackTrigger = (typeof FALLBACK_TRIGGERS)[number];
export type GreetingScope = (typeof GREETING_SCOPES)[number];
export type Flow = (typeof FLOWS)[number];
export type ControllerState = 'enabled' | 'disabled';

// The contract's Static types widen these code lists to string; the readers check every value.
export interface Voicemail extends Omit<C.Voicemail, 'state'> { state: VoicemailState }
export interface Greeting extends Omit<C.VoicemailGreeting, 'scope'> { scope: GreetingScope }
export interface RuleVoicemail { rule_id: string; voicemail_on: FallbackTrigger[]; version: number }
export interface VoicemailEvaluation {
  target: boolean;
  voicemail_on: FallbackTrigger[];
  greeting: { id: string; scope: GreetingScope; duration_ms: number } | null;
  flow: Flow;
  controller: ControllerState;
  reason_code: string | null;
  detail: string;
}

// ---- Readers ---------------------------------------------------------------------------------------------

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
const E164 = /^\+[1-9][0-9]{6,14}$/;
const SHA = /^[a-f0-9]{64}$/;
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function oneOf<T extends string>(list: readonly T[], value: unknown): T | null {
  return typeof value === 'string' && (list as readonly string[]).includes(value) ? (value as T) : null;
}
const id = (value: unknown): string | null => (typeof value === 'string' && ID.test(value) ? value : null);
const int = (value: unknown, min: number, max = Number.MAX_SAFE_INTEGER): number | null => (typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : null);
const time = (value: unknown): string | null => (typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null);
const triggers = (value: unknown): FallbackTrigger[] | null => {
  if (!Array.isArray(value)) return null;
  const list = value.map(v => oneOf(FALLBACK_TRIGGERS, v));
  return list.every((v): v is FallbackTrigger => v !== null) ? FALLBACK_TRIGGERS.filter(t => list.includes(t)) : null;
};

export function readVoicemail(value: unknown): Voicemail | null {
  if (!isRecord(value)) return null;
  const vmId = id(value.id), callId = id(value.call_id), recordingId = id(value.recording_id), state = oneOf(STATES, value.state);
  const duration = int(value.duration_ms, 0), version = int(value.version, 1), created = time(value.created_at);
  if (!vmId || !callId || !recordingId || !state || duration === null || version === null || !created || typeof value.recording_available !== 'boolean') return null;
  const from = typeof value.from_e164 === 'string' && E164.test(value.from_e164) ? value.from_e164 : null;
  return {
    id: vmId, call_id: callId, recording_id: recordingId, number_id: id(value.number_id), from_e164: from, greeting_id: id(value.greeting_id),
    duration_ms: duration, state, listened_by: id(value.listened_by), listened_at: time(value.listened_at), archived_at: time(value.archived_at),
    recording_status: typeof value.recording_status === 'string' ? value.recording_status.slice(0, 40) : '',
    recording_available: value.recording_available, created_at: created, version,
  };
}

export function readGreeting(value: unknown): Greeting | null {
  if (!isRecord(value)) return null;
  const gId = id(value.id), scope = oneOf(GREETING_SCOPES, value.scope), bytes = int(value.bytes, 44, LIMITS.greetingMaxBytes);
  const duration = int(value.duration_ms, LIMITS.greetingMinMs, LIMITS.greetingMaxMs), uploadedBy = id(value.uploaded_by), created = time(value.created_at), version = int(value.version, 1);
  if (!gId || !scope || bytes === null || duration === null || !uploadedBy || !created || version === null || typeof value.active !== 'boolean') return null;
  return {
    id: gId, scope, number_id: id(value.number_id), ring_group_id: id(value.ring_group_id), bytes, sha256: typeof value.sha256 === 'string' && SHA.test(value.sha256) ? value.sha256 : '',
    duration_ms: duration, uploaded_by: uploadedBy, created_at: created, active: value.active, version,
  };
}

export function readRuleVoicemail(value: unknown): RuleVoicemail | null {
  if (!isRecord(value)) return null;
  const ruleId = id(value.rule_id), on = triggers(value.voicemail_on), version = int(value.version, 1);
  return ruleId && on && version !== null ? { rule_id: ruleId, voicemail_on: on, version } : null;
}

/** The dry run's `voicemail` answer, or null when the response has none (an API before migration 015) or it can't be read. */
export function readVoicemailEvaluation(value: unknown): VoicemailEvaluation | null {
  if (!isRecord(value)) return null;
  const on = triggers(value.voicemail_on), flow = oneOf(FLOWS, value.flow), controller = oneOf(['enabled', 'disabled'] as const, value.controller);
  if (typeof value.target !== 'boolean' || !on || !flow || !controller || typeof value.detail !== 'string') return null;
  let greeting: VoicemailEvaluation['greeting'] = null;
  if (isRecord(value.greeting)) {
    const gId = id(value.greeting.id), scope = oneOf(GREETING_SCOPES, value.greeting.scope), duration = int(value.greeting.duration_ms, LIMITS.greetingMinMs, LIMITS.greetingMaxMs);
    if (!gId || !scope || duration === null) return null;
    greeting = { id: gId, scope, duration_ms: duration };
  } else if (value.greeting !== null && value.greeting !== undefined) return null;
  const reason = typeof value.reason_code === 'string' && /^[a-z][a-z0-9_]{0,99}$/.test(value.reason_code) ? value.reason_code : null;
  return { target: value.target, voicemail_on: on, greeting, flow, controller, reason_code: reason, detail: value.detail.slice(0, 500) };
}

/** Reads a list, dropping rows this version can't read (counted in `skipped`). */
export function readRows<T>(value: unknown, read: (row: unknown) => T | null): { rows: T[]; skipped: number } | null {
  if (!Array.isArray(value)) return null;
  const rows: T[] = [];
  let skipped = 0;
  for (const row of value) {
    const parsed = read(row);
    if (parsed) rows.push(parsed);
    else skipped += 1;
  }
  return { rows, skipped };
}

/** The page's next cursor: `page.next_cursor` and `meta.next_cursor` carry the same value; either is accepted. */
export function nextCursorOf(envelope: unknown): string | null {
  if (!isRecord(envelope)) return null;
  const page = isRecord(envelope.page) ? envelope.page.next_cursor : undefined;
  const meta = isRecord(envelope.meta) ? envelope.meta.next_cursor : undefined;
  const cursor = typeof page === 'string' ? page : typeof meta === 'string' ? meta : null;
  return cursor && cursor.length <= 1000 ? cursor : null;
}

// ---- Faults ----------------------------------------------------------------------------------------------

export interface VoicemailFault extends ErrorView {
  status: number;
  /** The server's own message (safe by contract). */
  serverMessage: string;
  /** A change may have reached the server without an answer. */
  outcomeUnknown: boolean;
}

export type Result<T> = { ok: true; value: T } | { ok: false; fault: VoicemailFault };

/** Plain copy for voicemail codes; the server's message stays on the fault (and is shown next to the control). */
export const VOICEMAIL_COPY: Readonly<Record<string, string>> = Object.freeze({
  permission_denied: 'Your role doesn’t allow this.',
  version_conflict: 'Someone else changed this since you opened it. Reload to see the latest, then try again.',
  voicemail_archived: 'Someone archived this voicemail already.',
  not_found: 'This was deleted, or it isn’t shared with you any more.',
  voicemail_schema_unavailable: 'Voicemail isn’t installed on the server yet.',
  invalid_cursor: 'The list changed while you were reading it. It was reloaded from the newest message.',
  greeting_invalid_wav: 'The server couldn’t read this file as a WAV recording.',
  greeting_format_unsupported: 'The server only accepts 8 kHz, 16-bit, mono WAV greetings.',
  greeting_too_short: 'A greeting must be at least 1 second long.',
  greeting_too_long: 'A greeting can be at most 60 seconds long.',
  greeting_too_large: 'A greeting file can be at most 1 MiB.',
  unsupported_media_type: 'The server only accepts greetings sent as WAV audio.',
  greeting_storage_unavailable: 'The server couldn’t store the greeting right now. Nothing was changed. Try again later.',
  invalid_response: 'The server sent voicemail data this version can’t read.',
});

export function toFault(error: unknown, fallbackTitle: string): VoicemailFault {
  if (isFault(error)) return error;
  const view = describeError(error, fallbackTitle);
  const e = isApiRequestError(error) ? error : null;
  const copy = e ? VOICEMAIL_COPY[e.code] : undefined;
  return {
    ...view,
    title: e?.outcomeUnknown ? 'The server didn’t answer. The change may or may not have been made. Reload to check.' : copy ?? view.title,
    status: e?.status ?? 0,
    serverMessage: e?.message ?? '',
    outcomeUnknown: e?.outcomeUnknown ?? false,
  };
}

export const unreadable = (): VoicemailFault => ({ title: VOICEMAIL_COPY.invalid_response!, detail: '', code: 'invalid_response', requestId: null, retryable: true, status: 200, serverMessage: '', outcomeUnknown: false });

export function isFault(value: unknown): value is VoicemailFault {
  return typeof value === 'object' && value !== null && 'serverMessage' in value && 'outcomeUnknown' in value && !(value instanceof Error);
}

/** A 403 that means the role can't do this (not an MFA or CSRF refusal, which the shell handles). */
export const isRoleRefusal = (fault: VoicemailFault) => fault.status === 403 && fault.code !== 'mfa_required' && fault.code !== 'csrf_invalid';
export const isSchemaMissing = (fault: VoicemailFault) => fault.status === 503 && fault.code === 'voicemail_schema_unavailable';

// ---- Copy ------------------------------------------------------------------------------------------------

export const STATE_LABEL: Record<VoicemailState, string> = { new: 'New', listened: 'Listened', archived: 'Archived' };
export const STATE_FILTER_LABEL: Record<'' | VoicemailState, string> = { '': 'New and listened', new: 'New only', listened: 'Listened only', archived: 'Archived' };

export const EMPTY_SENTENCE = 'Voicemails appear here when a routing rule sends a call to voicemail and the caller leaves a message of at least one second.';
export const SCHEMA_SENTENCE = 'The server’s voicemail storage hasn’t been set up. Calls aren’t affected. Voicemails appear here once an administrator installs it.';

export const TRIGGER_LABEL: Record<FallbackTrigger, string> = {
  no_answer: 'If nobody answers',
  busy: 'If everyone is busy',
  unavailable: 'If nobody can be reached',
};
export const TRIGGER_HINT: Record<FallbackTrigger, string> = {
  no_answer: 'Someone was rung and nobody picked up.',
  busy: 'Everyone who could take the call was on another call, after the busy fallback ran.',
  unavailable: 'No phone was online or everyone had do not disturb on, after the unavailable fallback ran.',
};
export const CLOSED_NOTE = 'Outside open hours and on holidays, calls go to voicemail only when “When closed or on a holiday” is set to Voicemail.';

/** The greeting notice the brief requires, shown wherever a greeting is uploaded or recorded. */
export const GREETING_LEGAL_NOTICE = 'What the greeting says is your workspace’s responsibility. It should tell callers that their message will be recorded. Without a greeting, callers hear only a beep and are recorded with no spoken notice.';

const seconds = (ms: number) => `${Math.round(ms / 1000)} second${Math.round(ms / 1000) === 1 ? '' : 's'}`;

/** Where the greeting a voicemail would play comes from. */
export const SCOPE_PHRASE: Record<GreetingScope, string> = { number: 'this number’s greeting', ring_group: 'the ring group’s greeting', tenant_default: 'the workspace greeting' };

/** The dry run's voicemail flow in one plain headline. */
export function flowHeadline(e: VoicemailEvaluation): string {
  if (e.controller === 'disabled') return 'Voicemail controller disabled: calls are logged as missed';
  if (e.flow === 'missed_call') return e.reason_code === 'voicemail_greeting_required' ? 'Voicemail needs a greeting: calls are logged as missed' : 'Calls are logged as missed';
  if (e.flow === 'greeting_beep_record') return 'Greeting, beep, then the message is recorded';
  return 'Beep, then the message is recorded';
}

/** What a caller who reaches voicemail gets, in plain words. */
export function flowSentence(e: VoicemailEvaluation): string {
  if (e.controller === 'disabled') return 'The voicemail service on the server isn’t running yet, so a call sent to voicemail is logged as a missed call and nothing is recorded. Greetings and settings are kept for when it runs.';
  if (e.flow === 'missed_call') return e.reason_code === 'voicemail_greeting_required' ? 'The server is set to record voicemail only after a greeting, and none is set, so these calls are logged as missed calls. Upload a greeting for this number to turn voicemail on.' : 'Calls sent to voicemail are logged as missed calls.';
  if (e.flow === 'greeting_beep_record' && e.greeting) return `The caller hears ${SCOPE_PHRASE[e.greeting.scope]} (${seconds(e.greeting.duration_ms)}), then a beep, and can leave a message of up to ${LIMITS.recordMaxSeconds / 60} minutes.`;
  return `No greeting is set, so the caller hears only a beep and is recorded with no spoken notice (up to ${LIMITS.recordMaxSeconds / 60} minutes).`;
}

/** When this rule sends calls to voicemail at all. */
export function usageSentence(e: VoicemailEvaluation): string {
  const when = e.voicemail_on.map(t => TRIGGER_LABEL[t].replace(/^If /, '').toLowerCase());
  const list = when.length > 1 ? `${when.slice(0, -1).join(', ')} or ${when.at(-1)}` : when[0] ?? '';
  if (e.target) return when.length ? `At this time the call goes straight to voicemail. On other results it also goes to voicemail if ${list}.` : 'At this time the call goes straight to voicemail.';
  if (when.length) return `The call goes to voicemail instead of being logged as missed if ${list}.`;
  return 'This rule doesn’t send calls to voicemail.';
}

/** The voicemail service state learned from a dry run in this session (null: not checked yet). */
export type KnownController = ControllerState | null;

/** The note under a voicemail choice in the rule editor and the greeting panel. Never claims more than the server said. */
export function serviceNote(known: KnownController): string {
  if (known === 'disabled') return 'The voicemail service on the server isn’t running yet, so these calls are logged as missed calls. Settings and greetings are kept for when it runs.';
  if (known === 'enabled') return 'The server’s last preview said the voicemail service is running. Preview a rule to see exactly what callers get.';
  return 'Calls are recorded only while the server’s voicemail service is running; otherwise they’re logged as missed calls. Preview a rule to see which applies now.';
}
